/**
 * live-since-mount-store · per-(QueryClient, queryHash) provenance log of
 * rowKeys that arrived via a `prepend:` realtime recipe.
 *
 * The codegen-emitted apply-event reducer calls `tagLiveRow(qc, queryHash,
 * rowKey)` from the new-row branch of every `prepend:` recipe (NOT the
 * replace-if-present branch — a follow-up event on an existing row is a
 * field patch, not a new arrival). Each tag bumps a per-queryHash monotonic
 * sequence number; the rowKey is appended to a bounded ring.
 *
 * Each list-hook instance captures the current `seq` at the moment its
 * snapshot lands (`useRealtimeQuery.snapshotLandedRef`) as its mount fence,
 * then projects `liveSinceMountIds` as the rowKeys whose tag-seq exceeds
 * that fence and still appear in the cached `ids` array.
 *
 * Persisted-cache restore writes through `markNextWrite('snapshot')` and
 * never enters this store; REST page fetches mark `'rest'`. Only the
 * codegen-emitted prepend chokepoint, optimistic mutation onMutate writes,
 * and the cross-tab broadcast apply path tag here — keeping the
 * "what counts as live" rule explicit at the call sites rather than as an
 * after-the-fact enum check.
 *
 * Platform-agnostic (SDK core, web + RN): a module-level WeakMap keyed on
 * the QueryClient instance, no platform APIs. Mirrors `snapshot-seq-store`
 * and the devtool `cache-write` marker store.
 */

import type { QueryClientLike } from '../devtool/channels/cache-write.js';
import { _bumpPerf } from '../devtool/perf-counters.js';

/**
 * Per-queryHash live-row log.
 *
 * `seq` is a monotonic counter that ticks once per tag. `rows` is the
 * ordered (oldest → newest) ring of `(rowKey, tagSeq)` tuples bounded by
 * `MAX_LIVE_ROWS`. The mount fence comparison uses `tagSeq`, so evicted
 * rows compare as "older than any reasonable fence" and never animate
 * spuriously.
 */
interface LiveRowLog {
  seq: number;
  rows: Array<[rowKey: string, tagSeq: number]>;
}

/** Two-page headroom for typical pageSize=200 list hooks; oldest pruned first. */
const MAX_LIVE_ROWS = 256;

/** One log map per QueryClient. WeakMap prevents leaks across test/router resets. */
const logs = new WeakMap<QueryClientLike, Map<string, LiveRowLog>>();

function getQueryClientMap(qc: QueryClientLike): Map<string, LiveRowLog> {
  let m = logs.get(qc);
  if (m === undefined) {
    m = new Map<string, LiveRowLog>();
    logs.set(qc, m);
  }
  return m;
}

function getLog(qc: QueryClientLike, queryHash: string): LiveRowLog {
  const byHash = getQueryClientMap(qc);
  let log = byHash.get(queryHash);
  if (log === undefined) {
    log = { seq: 0, rows: [] };
    byHash.set(queryHash, log);
  }
  return log;
}

/**
 * Append `rowKey` to the live-row log for `(qc, queryHash)` and bump the
 * monotonic sequence. NOT idempotent: each call appends a fresh entry —
 * the caller (`tagNewRowsFromDiff`) is responsible for not double-tagging
 * (which it does via a prev/next diff with Set-based dedup).
 *
 * Returns the new sequence number — callers MAY use this for instrumentation;
 * the snapshot capture in `useRealtimeQuery` reads via `readLiveSeq`.
 */
export function tagLiveRow(qc: QueryClientLike, queryHash: string, rowKey: string): number {
  const log = getLog(qc, queryHash);
  log.seq += 1;
  log.rows.push([rowKey, log.seq]);
  if (process.env.NODE_ENV !== 'production') {
    _bumpPerf('liveSinceMount.tagged');
  }
  if (log.rows.length > MAX_LIVE_ROWS) {
    const dropped = log.rows.length - MAX_LIVE_ROWS;
    log.rows.splice(0, dropped);
    if (process.env.NODE_ENV !== 'production') {
      _bumpPerf('liveSinceMount.dropped', dropped);
    }
  }
  return log.seq;
}

/**
 * Read the current monotonic sequence for `(qc, queryHash)`. A consumer
 * captures this once when its snapshot lands and uses it as its mount
 * fence. `0` is returned when no rows have been tagged yet — any tag
 * thereafter will compare strictly greater.
 */
export function readLiveSeq(qc: QueryClientLike, queryHash: string): number {
  const byHash = logs.get(qc);
  if (byHash === undefined) return 0;
  const log = byHash.get(queryHash);
  return log === undefined ? 0 : log.seq;
}

/**
 * Project the rowKeys tagged strictly after `fenceSeq`, in insertion order
 * (oldest → newest). The caller intersects this with its current `ids`
 * array to drop rows that have since fallen off the page cap.
 *
 * Returns an empty array when nothing matches — caller is responsible for
 * memoising the projection if it needs identity stability across renders.
 */
export function getLiveRowsSince(
  qc: QueryClientLike,
  queryHash: string,
  fenceSeq: number,
): readonly string[] {
  const byHash = logs.get(qc);
  if (byHash === undefined) return EMPTY;
  const log = byHash.get(queryHash);
  if (log === undefined) return EMPTY;
  const out: string[] = [];
  for (let i = 0; i < log.rows.length; i += 1) {
    const entry = log.rows[i];
    if (entry === undefined) continue;
    if (entry[1] > fenceSeq) out.push(entry[0]);
  }
  return out;
}

const EMPTY: readonly string[] = [];

/**
 * Identity-stable projection cache for `liveSinceMountIds`. The list-hook
 * factory reads `liveSinceMountIds` via a getter on the result Proxy; the
 * getter must return the SAME array reference across renders when the
 * projection set hasn't changed, otherwise every `useEffect([…ids])` on
 * the consumer side churns on every NATS event.
 *
 * Keyed by `(QueryClient, queryHash, fenceSeq)`. Cache hit when:
 *   - the per-queryHash live log seq is unchanged since the last call, AND
 *   - the consumer's `ids` array reference is unchanged.
 *
 * Cache miss recomputes the projection (filtering `getLiveRowsSince` by
 * membership in `ids`), stores it under the cache entry, and returns it.
 */
interface ProjectionCache {
  lastLiveSeq: number;
  lastIdsRef: readonly string[] | null;
  projection: readonly string[];
}

/**
 * Per-queryHash cap on cached projection entries. A long-lived QueryClient
 * accumulates one (queryHash, fenceSeq) entry per consumer-mount over the
 * session; without a cap, a chatty navigation pattern (mount → unmount →
 * remount of the same hook) would grow the map without bound. Maps iterate
 * in insertion order so we prune the OLDEST same-queryHash entries on
 * overflow — the active consumer's fence is always the most-recently
 * inserted, so it stays cached.
 */
const MAX_PROJECTIONS_PER_QUERY_HASH = 4;

const projections = new WeakMap<QueryClientLike, Map<string, ProjectionCache>>();

export interface LiveSinceMountResourceUsage {
  readonly logQueryHashes: number;
  readonly loggedRows: number;
  readonly projectionQueryHashes: number;
  readonly projectionEntries: number;
}

/** Pull-only count snapshot; does not allocate either outer per-client map. */
export function getLiveSinceMountResourceUsage(qc: object): LiveSinceMountResourceUsage {
  const key = qc as QueryClientLike;
  const logMap = logs.get(key);
  const projectionMap = projections.get(key);
  let loggedRows = 0;
  for (const log of logMap?.values() ?? []) loggedRows += log.rows.length;
  const projectionQueryHashes = new Set<string>();
  for (const cacheKey of projectionMap?.keys() ?? []) {
    const separator = cacheKey.lastIndexOf(':');
    projectionQueryHashes.add(separator < 0 ? cacheKey : cacheKey.slice(0, separator));
  }
  return {
    logQueryHashes: logMap?.size ?? 0,
    loggedRows,
    projectionQueryHashes: projectionQueryHashes.size,
    projectionEntries: projectionMap?.size ?? 0,
  };
}

function getProjectionMap(qc: QueryClientLike): Map<string, ProjectionCache> {
  let m = projections.get(qc);
  if (m === undefined) {
    m = new Map<string, ProjectionCache>();
    projections.set(qc, m);
  }
  return m;
}

/**
 * Compute (or read-from-cache) the identity-stable projection of
 * `liveSinceMountIds` for one `(qc, queryHash, fenceSeq, ids)` tuple.
 *
 * Returns an empty array (the shared `EMPTY`) when nothing has been tagged
 * past the fence — so a freshly-mounted consumer with no live rows yet sees
 * a stable empty array reference, not a fresh allocation per render.
 *
 * Existing-entry misses depend on the consumer IDs and bounded live log;
 * hits are O(1). New fence entries also scan projections to enforce the cap.
 */
export function projectLiveSinceMountIds(
  qc: QueryClientLike,
  queryHash: string,
  fenceSeq: number,
  ids: readonly string[],
): readonly string[] {
  const map = getProjectionMap(qc);
  const cacheKey = `${queryHash}:${fenceSeq}`;
  const currentLiveSeq = readLiveSeq(qc, queryHash);
  const cached = map.get(cacheKey);
  if (cached !== undefined && cached.lastLiveSeq === currentLiveSeq && cached.lastIdsRef === ids) {
    return cached.projection;
  }
  const liveRows = getLiveRowsSince(qc, queryHash, fenceSeq);
  let projection: readonly string[];
  if (liveRows.length === 0) {
    projection = EMPTY;
  } else {
    const idSet = new Set(ids);
    const out: string[] = [];
    for (let i = 0; i < liveRows.length; i += 1) {
      const id = liveRows[i];
      if (id !== undefined && idSet.has(id)) out.push(id);
    }
    projection = out.length === 0 ? EMPTY : out;
  }
  // Re-insert overwrites the existing entry (preserves Map insertion-order
  // semantics for the LRU prune below — the just-touched key becomes
  // "newest"). Delete first to ensure re-insertion is treated as new.
  map.delete(cacheKey);
  map.set(cacheKey, { lastLiveSeq: currentLiveSeq, lastIdsRef: ids, projection });
  // Replacing an entry refreshes recency without increasing its query's count.
  if (cached !== undefined) return projection;
  // Per-queryHash LRU prune. Walk Map keys (insertion order = LRU order),
  // count same-queryHash entries, drop the oldest until at most
  // MAX_PROJECTIONS_PER_QUERY_HASH remain.
  const prefix = `${queryHash}:`;
  let count = 0;
  for (const k of map.keys()) {
    if (k.startsWith(prefix)) count += 1;
  }
  if (count > MAX_PROJECTIONS_PER_QUERY_HASH) {
    let toDrop = count - MAX_PROJECTIONS_PER_QUERY_HASH;
    for (const k of map.keys()) {
      if (toDrop === 0) break;
      if (k.startsWith(prefix)) {
        map.delete(k);
        toDrop -= 1;
      }
    }
  }
  return projection;
}

/**
 * Diff `prev.data` vs `next.data` on `rowKey`, tagging every rowKey present
 * in `next` but absent from `prev` into the per-(qc, queryHash) log.
 *
 * The single tag chokepoint for the `liveSinceMountIds` projection — called
 * by `useRealtimeQuery`'s `applyEvent` call sites after each `setQueryData`.
 * Pure cache diff: works for one-row `prepend:` recipes, future recipes
 * that batch multiple rows in a single event, and bails on `set:` / replace-
 * in-place recipes (no new rowKey, nothing tagged).
 *
 * Cheap: a Set built from `prev.data` rowKeys, then a single pass over
 * `next.data`. Bails on non-paged shapes (single-row hooks have no `.data`
 * array and never reach here in practice — the caller skips this branch
 * when `rowKey` is undefined).
 */
export function tagNewRowsFromDiff(
  qc: QueryClientLike,
  queryHash: string,
  rowKey: string,
  prev: unknown,
  next: unknown,
): void {
  // Normalized feeds (MOR-859) store the id list directly; classic feeds store
  // row objects whose `rowKey` field is the id.
  const idOf = (r: unknown): string | undefined => {
    if (typeof r === 'string') return r.length > 0 ? r : undefined;
    if (r === null || typeof r !== 'object') return undefined;
    const id = (r as Record<string, unknown>)[rowKey];
    return typeof id === 'string' && id.length > 0 ? id : undefined;
  };
  if (prev === null || typeof prev !== 'object') {
    // No prev — treat every row in `next` as new.
    if (next === null || typeof next !== 'object') return;
    const nextData = (next as { readonly data?: unknown }).data;
    if (!Array.isArray(nextData)) return;
    for (let i = 0; i < nextData.length; i += 1) {
      const id = idOf(nextData[i]);
      if (id !== undefined) tagLiveRow(qc, queryHash, id);
    }
    return;
  }
  if (next === null || typeof next !== 'object') return;
  const prevData = (prev as { readonly data?: unknown }).data;
  const nextData = (next as { readonly data?: unknown }).data;
  if (!Array.isArray(prevData) || !Array.isArray(nextData)) return;
  if (nextData.length === 0) return;
  const seen = new Set<string>();
  for (let i = 0; i < prevData.length; i += 1) {
    const id = idOf(prevData[i]);
    if (id !== undefined) seen.add(id);
  }
  for (let i = 0; i < nextData.length; i += 1) {
    const id = idOf(nextData[i]);
    if (id !== undefined && !seen.has(id)) tagLiveRow(qc, queryHash, id);
  }
}

/** Drop the live-row log for one queryHash — used by query-level resets. */
export function clearLiveRows(qc: QueryClientLike, queryHash: string): void {
  const byHash = logs.get(qc);
  if (byHash !== undefined) byHash.delete(queryHash);
  const projMap = projections.get(qc);
  if (projMap !== undefined) {
    for (const k of projMap.keys()) {
      if (k.startsWith(`${queryHash}:`)) projMap.delete(k);
    }
  }
}

/**
 * Sidecar map from a `useRealtimeQuery` return value (the TanStack query
 * result Proxy + optional `ref`) to that hook instance's mount-fence seq.
 *
 * WHY A SIDECAR. Attaching the fence as a property of the query result
 * (via spread or assignment) defeats TanStack v5's tracked-properties
 * optimisation for single-row hooks: spreading a Proxy triggers `get` on
 * every enumerable, marking each as "tracked," which forces re-render on
 * every prop change. List hooks override this via `notifyOnChangeProps`,
 * but single-row hooks rely on lazy tracking — the sidecar lets the SDK
 * thread the fence to `createListHook` without touching the result.
 *
 * WeakMap auto-clears entries when the query result is GC'd.
 */
const resultFences = new WeakMap<object, number | null>();

/** Stash the fence for one hook instance's query result. Called from `useRealtimeQuery`. */
export function attachFenceToResult(result: object, fence: number | null): void {
  resultFences.set(result, fence);
}

/** Read the fence stashed by `attachFenceToResult` — `null` until the snapshot lands. */
export function readFenceFromResult(result: object): number | null {
  const v = resultFences.get(result);
  return v === undefined ? null : v;
}

/** Drop everything — hooked by the logout-cache-clear path. */
export function clearAllLiveRows(qc: QueryClientLike): void {
  const byHash = logs.get(qc);
  if (byHash !== undefined) byHash.clear();
  const projMap = projections.get(qc);
  if (projMap !== undefined) projMap.clear();
}
